<template>
  <TrackingProvider>
    <Head>
      <meta
        v-if="!allowRobots"
        name="robots"
        content="none"
      >
    </Head>

    <div
      ref="appRoot"
      class="flex flex-col items-stretch min-h-screen bg-vp-base-100"
    >
      <hgroup
        class="inset-x-0 top-0 text-vp-base-900 checkout border-b border-vp-base-200 font-bold flex justify-center h-16"
      >
        <header class="container mx-auto p-3 flex justify-between items-center">
          <div class="flex items-center gap-4">
            <div
              v-if="headerLogo"
              class="h-12 overflow-hidden"
            >
              <img
                :alt="`${headerTitle} logo`"
                :src="headerLogo"
                class="object-contain h-full max-h-full max-w-full"
              >
            </div>

            <VPCountdown
              v-if="targetTime"
              :target-time="targetTime"
              @expired="onCountdownExpired"
            />
          </div>
          <div class="flex flex-col items-end">
            <a
              href="https://my.venuepilot.co"
              target="_blank"
              class="text-xs sm:text-sm font-normal"
            >
              {{ $gettext('Find my tickets') }}
              <iconify-icon
                icon="mdi:open-in-new"
                class="text-sm -mb-0.5"
              />
            </a>
            <button
              v-if="showNeedHelp"
              type="button"
              class="xl:hidden text-sm"
              @click="activateHubspotChat"
            >
              {{ $gettext('Need help?') }}
            </button>
          </div>
        </header>
      </hgroup>
      <main class="container mx-auto flex justify-center lg:grow">
        <slot />
      </main>
    </div>
    <VPTurnstileModal />
    <CartExpiredModal
      :show="cartExpired"
      @update:show="onExpiryAcknowledged"
    />
  </TrackingProvider>
</template>

<script setup lang="ts">
import { Head } from 'vike-vue/Head';
import 'vue-toastification/dist/index.css';
import '../../checkout.css';
import { useGettext } from 'vue3-gettext';
import VPCountdown from '../../components/VPCountdown.vue';
import { useCheckoutStore } from '../../stores/checkout.ts';
import { computed, ref, watch, watchEffect } from 'vue';
import { useConfig } from 'vike-vue/useConfig';
import { useCheckoutTitle } from '../../useCheckoutTitle.ts';
import TrackingProvider from '../../components/tracking/TrackingProvider.vue';
import { VPTurnstileModal } from '@vp/vue-components';
import { useRuntimeConfig } from '../../composables/useRuntimeConfig';
import { redirect } from 'vike/abort';
import { navigate } from 'vike/client/router';
import { usePageContext } from 'vike-vue/usePageContext';
import { useRouter } from 'vue-router';
import * as Sentry from '@sentry/vue';
import { isFramed } from '../../utils/isFramed';
import { useParentFrameSync } from '../../composables/useParentFrameSync';
import CartExpiredModal from '../../components/CartExpiredModal.vue';

const { $gettext } = useGettext();
const config = useConfig();
const { title } = useCheckoutTitle();
const { config: runtimeConfig } = useRuntimeConfig();
const showNeedHelp = computed(() => runtimeConfig.value?.showNeedHelp ?? false);

// While framed, report this element's height to the embedding page so it can
// auto-size the frame. Viewport-height utilities (min-h-screen here, and others
// deeper in the tree) are neutralised via the `vp-checkout-framed` marker the
// composable sets on <html> -- otherwise they equal the frame height we just set
// and feed back into an unbounded resize loop. See useParentFrameSync and
// checkout.css.
const appRoot = ref<HTMLElement | null>(null);
useParentFrameSync(appRoot);

watch(
  () => title.value,
  newValue => {
    config({ title: newValue });
  },
);

const checkoutStore = useCheckoutStore();

const pageContext = usePageContext();
const router = useRouter();

const cartExpired = ref(false);

const eventIdParam = computed(() => pageContext.routeParams?.event_id);

const pathFor = (name: string) => {
  const eventId = eventIdParam.value;
  if (!eventId) {
    return undefined;
  }

  return router.resolve({ name, params: { eventId } }).href;
};

// vike leaves a trailing slash on urlPathname and still routes the page; vue-router's
// href never has one. Comparing them raw fails open, and this gate guards a payment.
const samePath = (a?: string, b?: string) => {
  if (!a || !b) {
    return false;
  }

  return a.replace(/\/+$/, '') === b.replace(/\/+$/, '');
};

const selectionPath = computed(() => pathFor('OrderNew'));
const finalizing = computed(() => samePath(pageContext.urlPathname, pathFor('OrderFinalize')));

// The lock belongs to the pages where the buyer still holds a cart. This layout is shared
// with confirmation, which carries no event_id, and with finalize, where resetting would
// pull the ground from under a payment already with the processor.
const targetTime = computed(() => {
  if (!eventIdParam.value || finalizing.value || checkoutStore.paymentConfirming) {
    return undefined;
  }

  return checkoutStore.shoppingCart?.lockExpiresAt;
});

const goToSelection = async () => {
  const path = selectionPath.value;
  if (!path || samePath(pageContext.urlPathname, path)) {
    return;
  }

  try {
    await navigate(path);
  } catch (e) {
    Sentry.captureException(e);
  }
};

const onCountdownExpired = async () => {
  if (cartExpired.value) {
    return;
  }

  cartExpired.value = true;

  try {
    await checkoutStore.reset();
  } catch (e) {
    Sentry.captureException(e);
  }

  await goToSelection();
};

const onExpiryAcknowledged = async (show: boolean) => {
  if (show) {
    return;
  }

  cartExpired.value = false;
  await goToSelection();
};

const allowRobots = import.meta.env.VITE_ALLOW_ROBOTS == '1';

const headerTitle = computed(() => checkoutStore.getSelectedEvent?.venue?.name || 'VenuePilot');
const headerLogo = computed(() => {
  const venue = checkoutStore.getSelectedEvent?.venue;
  const variants = venue?.logo?.variants;

  if (!variants || variants.length == 0) {
    return venue?.logoUrl;
  }

  const variant = variants.find(v => v.variant == 'cover_xs') || variants[0];

  return variant.src;
});

const activateHubspotChat = () => {
  if (window.HubSpotConversations) {
    document.body.setAttribute('data-unhide-hubspot-messages', '1');
    window.HubSpotConversations.widget.open();

    window.HubSpotConversations.on('widgetClosed', () => document.body.setAttribute('data-unhide-hubspot-messages', '0'));
  }
};

watchEffect(() => {
  if (typeof window === 'undefined') {
    return;
  }

  const event = checkoutStore.getSelectedEvent;
  if (!event || !event.canonicalUrl) {
    return;
  }

  // An embed deliberately chose the origin it framed. Redirecting to the
  // canonical host would navigate the frame out from under the host page, and
  // because partitioned cookies are keyed by (top-level site, cookie origin),
  // it would also strand the session and cart established on the framed origin.
  if (isFramed()) {
    return;
  }

  const current = new URL(window.location.href);
  const canonical = new URL(event.canonicalUrl);

  if (current.origin !== canonical.origin) {
    current.searchParams.forEach((value, key) => {
      canonical.searchParams.append(key, value);
    });

    throw redirect(canonical.href);
  }
});
</script>
